import type { Handlers } from '~/resources/tracking'

export const useTrackingStore = defineStore('tracking', () => {
  const params = useParams()
  const config = useRuntimeConfig()
  const consent = useConsent()
  const isFreeweek = useFlag('freeweek')
  const { gtag } = useGtag()

  const options = ref<{
    handlers?: Record<string, Function>
    disabled?: boolean
    gtmID?: string
  }>({})
  const handlers = ref<Handlers>()

  function init(h: Handlers, pageloadHandler: Function) {
    if (import.meta.server || isFreeweek.value) return

    window.dataLayer = window.dataLayer || []

    handlers.value = h

    options.value = { ...config.public.tracking, h }

    trackPageLoad(pageloadHandler(config.public.tracking.gtmID))

    if (consent) {
      const initialConsent = consent.consent
      if (initialConsent) {
        trackConsentDefault(initialConsent)
      }
    }

    if (
      !options.value.disabled &&
      params.all.tracking !== false &&
      options.value.gtmID
    ) {
      loadGTM(options.value.gtmID)
    }
  }

  function send(
    element:
      | TrackingElement
      | TrackingElement[]
      | Record<string, unknown>
      | Record<string, unknown>[],
  ) {
    if (!Array.isArray(element)) {
      element = [element]
    }
    ;(element as Array<Record<string, unknown>>).forEach((e) => {
      if (window.dataLayer && !options.value.disabled) {
        dataLayerPush(e)
      }
    })
  }

  function trackPageLoad(event: Pagedata) {
    const pageLoadEvents = window.dataLayer.filter((e) => isPageLoadEvent(e))

    if (pageLoadEvents.length) {
      // Tracking the page is loaded only when the route changes
      if (pageLoadEvents.at(-1)?.page.virtPath === event.page.virtPath) return
      dataLayerPush(event as unknown as Record<string, unknown>)
    } else {
      // The first page view event should have `event: undefined` instead of `event: 'page_view'`.
      // See https://jira.migros.net/browse/IHGWEBCC-1145
      dataLayerPush({ ...event, event: undefined })
    }
  }

  function isPageLoadEvent(item: unknown): item is Pagedata {
    return (
      !!item &&
      typeof item === 'object' &&
      'page' in item &&
      'event' in item &&
      [undefined, 'page_view'].includes(item.event?.toString())
    )
  }

  function trackConsentDefault(consent: Consent) {
    // Tracking user consent only once at page load
    if (
      !window.dataLayer.some((item) => item.consentStatistics !== undefined)
    ) {
      dataLayerPush(createConsentEventData(consent))
    }

    // Tracking consent defaults only once at page load
    if (
      !window.dataLayer.some(
        (item) => item[0] === 'consent' && item[1] === 'default',
      )
    ) {
      gtag('consent', 'default', createConsentDetailedEventData(consent))
    }
  }

  function trackConsentUpdate(consent: Consent) {
    dataLayerPush({
      event: 'user consent',
      ...createConsentEventData(consent),
    })

    gtag('consent', 'update', createConsentDetailedEventData(consent))
    if (!consent.statistics) {
      gtag('set', 'url_passthrough', true)
    }
  }

  return { handlers, init, send, trackConsentUpdate }
})

function dataLayerPush<A extends any[] = unknown[]>(...args: A) {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(...(args as unknown as Record<string, unknown>[]))
}

function useGtag() {
  return {
    gtag:
      window?.gtag ??
      /**
       * Define the `gtag` function in case `gtm.js` is not loaded yet and `window.gtag` is unavailable.
       * @see https://developers.google.com/tag-platform/security/guides/consent?consentmode=advanced#tag-manager_3
       */
      function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer?.push(arguments as unknown as Record<string, unknown>)
      },
  }
}

function loadGTM(gtmID: string) {
  dataLayerPush({
    event: 'gtm.js',
    'gtm.start': new Date().getTime(),
    eventCallback(id: string) {
      if (id === gtmID && window.gaGlobal) {
        // Only set client id in projects that need it
        if (useIDs().client !== undefined) {
          useIDs().client = window.gaGlobal.vid
        }
      }
    },
  })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmID}`
  document.body.appendChild(script)
}

function createConsentEventData({ other, statistics }: Consent) {
  return {
    consentStatistics: statistics,
    consentRetargeting: other,
  }
}

function createConsentDetailedEventData({ other, statistics }: Consent) {
  return {
    ad_user_data: other ? 'granted' : 'denied',
    ad_personalization: other ? 'granted' : 'denied',
    ad_storage: other ? 'granted' : 'denied',
    analytics_storage: statistics ? 'granted' : 'denied',
  }
}
